import { useEffect, useRef, useState } from 'react';

const services = [
  {
    id: 'property-maintenance',
    title: 'Property Maintenance',
    description:
      'Horn Property Maintenance and Construction is your go-to for property care and construction services in Burlington and nearby areas. With a dedicated team and a commitment to excellence, we deliver top-quality solutions tailored to your needs and budget. From residential to commercial, we\'re here to exceed your expectations. Contact us today to learn more.',
    imageAlt: 'Professional lawn and property maintenance in Burlington Ontario',
    imageUrl:
      'https://public.readdy.ai/ai/img_res/897c36c41a641838c830c2c616ffd085.jpg',
    imageLeft: true,
  },
  {
    id: 'decks-fences',
    title: 'Decks and Fences',
    description:
      'At Horn Property Maintenance and Construction, we specialize in crafting outdoor spaces that enhance your home\'s beauty and functionality. With expert craftsmanship and a commitment to customer satisfaction, we deliver exceptional results. Trust us to create the perfect deck or fence to elevate your outdoor living experience.',
    imageAlt: 'Custom deck and fence construction in Burlington Ontario',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Beautiful%20newly%20constructed%20cedar%20wood%20backyard%20deck%20with%20pergola%20and%20outdoor%20patio%20furniture%2C%20natural%20warm%20wood%20tones%2C%20professional%20residential%20construction%20photography%2C%20afternoon%20sunlight%20casting%20gentle%20shadows%20on%20clean%20wood%20planks%2C%20Burlington%20Ontario%20suburban%20backyard%20setting&width=700&height=700&seq=svc2&orientation=squarish',
    imageLeft: false,
  },
  {
    id: 'home-renovations',
    title: 'Home Renovations',
    description:
      'At Horn Property Maintenance and Construction, we\'re passionate about transforming houses into dream homes. Our expert team specializes in delivering exceptional results, from kitchen remodels to bathroom renovations. With years of experience and a commitment to customer satisfaction, trust us to elevate your home with style and functionality.',
    imageAlt: 'Full kitchen renovation by Horn PMC Burlington',
    imageUrl:
      'https://readdy.ai/api/search-image?query=Modern%20bright%20kitchen%20renovation%20with%20white%20shaker%20cabinets%20quartz%20countertops%20stainless%20steel%20appliances%20hardwood%20floors%2C%20clean%20contemporary%20interior%20design%2C%20professional%20real%20estate%20photography%2C%20warm%20natural%20lighting%2C%20high%20end%20residential%20home%20renovation%20result%2C%20Hamilton%20Ontario%20Canada&width=700&height=700&seq=svc3&orientation=squarish',
    imageLeft: true,
  },
];

function ServiceBlock({
  service,
  index,
}: {
  service: (typeof services)[0];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const imageEl = (
    <div className="w-64 h-64 lg:w-80 lg:h-80 flex-shrink-0">
      <div className="w-full h-full rounded-full overflow-hidden border-4 border-[#3A7060]">
        <img
          src={service.imageUrl}
          alt={service.imageAlt}
          className="w-full h-full object-cover object-top"
          width="700"
          height="700"
          loading="lazy"
          decoding="async"
        />
      </div>
    </div>
  );

  const textEl = (
    <div className="flex-1 max-w-lg">
      <h3
        className="text-white text-3xl font-bold mb-5"
        style={{ fontFamily: 'Playfair Display, serif' }}
      >
        {service.title}
      </h3>
      <p
        className="text-white/75 text-sm leading-relaxed"
        style={{ fontFamily: 'Inter, sans-serif' }}
      >
        {service.description}
      </p>
    </div>
  );

  return (
    <div
      ref={ref}
      className={`flex flex-col lg:flex-row items-center gap-10 lg:gap-16 py-14 border-b border-[#3A7060]/30 last:border-0 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      {service.imageLeft ? (
        <>
          {imageEl}
          {textEl}
        </>
      ) : (
        <>
          <div className="lg:hidden">{imageEl}</div>
          {textEl}
          <div className="hidden lg:block">{imageEl}</div>
        </>
      )}
    </div>
  );
}

export default function Services() {
  return (
    <section id="services" className="bg-[#2B5741] py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-4">
          <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">
            WHAT WE DO
          </p>
          <h2
            className="text-white text-4xl font-bold mb-3"
            id="about-us"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            About Us
          </h2>
        </div>

        {services.map((service, i) => (
          <ServiceBlock key={service.id} service={service} index={i} />
        ))}

        <div className="text-center mt-8">
          <a
            href="#contact"
            className="inline-block bg-[#C8A97E] text-white px-10 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#b8976a] transition-colors whitespace-nowrap rounded-md"
          >
            LEARN MORE
          </a>
        </div>
      </div>
    </section>
  );
}
